import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.scss';
import AuthenticatedLayout from './layout/AuthenticatedLayout';
import JWTProtectedRoute from './layout/JWTProtectedRoute';
import PlaceholderPage from './pages/PlaceholderPage';
import DataStructurePage from './pages/data-structure-page/DataStructurePage';
import DataSetupPage from './pages/data-setup-page/DataSetupPage';
import Login from './pages/auth-pages/Login';
import Signup from './pages/auth-pages/Signup';
import SelectCompany from './pages/auth-pages/SelectCompany';
import MyInvites from './pages/auth-pages/MyInvites';
import ForgotPassword from './pages/auth-pages/ForgotPassword';
import RequestOtp from './pages/auth-pages/RequestOtp';
import VerifyOtp from './pages/auth-pages/VerifyOtp';
import ResetPassword from './pages/auth-pages/ResetPassword';
import SetPassword from './pages/auth-pages/SetPassword';
import CompanyCreation from './pages/auth-pages/CompanyCreation';
import BugReportPage from './pages/bug-report-page/BugReportPage';
import { accessibleNavItems, firstAccessiblePath } from './mocks/nav';
import { selectAdminModuleSlugs, selectIsOwner } from '@/redux/auth/selectors';
import i18n from './i18n/config';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { DEV_ONLY, HAND_SHAKE_MAX_RETRY, SKIP_ENCRYPT } from './config/config';
import ThemePage from './pages/theme-page/ThemePage';
import StructurePage from './pages/structure-page/StructurePage';
import RolesPage from './pages/roles-page/RolesPage';
import ProjectsPage from './pages/projects-page/ProjectsPage';
import ProjectDetailPage from './pages/projects-page/ProjectDetailPage';
import PeoplePage from './pages/people-pages/PeoplePage';
import RoleEditorPage from './pages/roles-page/RoleEditorPage';
import { useHandshakeMutation } from './api/endpoints/handshake';
import { handShakeFunc } from './config/security';
import { setSecurity } from '@/redux/security/securitySlice';
import DataSourcePage from './pages/datasource-page/DataSourcePage';
import InspectionPage from './pages/inspection-page/InspectionPage';
import FormTemplatesPage from './pages/form-templates-page/FormTemplatesPage';
import ReportsPage from './pages/reports-page/ReportsPage';
import FormBuilderPage from './pages/form-templates-page/FormBuilderPage';
import SubmissionsPage from './pages/submissions-page/SubmissionsPage';
import ReportViewPage from './pages/submissions-page/ReportViewPage';
import DataSourceEntriesPage from './pages/datasource-page/DataSourceEntriesPage';
import ActivityLogPage from './pages/activity-log-page/ActivityLogPage';
import TrashPage from './pages/trash/TrashPage';
import AttendancePage from './pages/attendance-page/AttendancePage';
import CompanySettingsPage from './pages/company-settings-page/CompanySettingsPage';
import MyProfilePage from './pages/my-profile-page/MyProfilePage';
import VerifyEmailPage from './pages/verify-email-page/VerifyEmailPage';

function App() {
  const mode = useAppSelector((state) => state.theme.mode);
  const security = useAppSelector((state) => state.security);
  // Module-gated routing: the same accessible-nav set that drives the sidebar
  // gates which in-shell routes render, and picks the default landing.
  const adminSlugs = useAppSelector(selectAdminModuleSlugs);
  const isOwner = useAppSelector(selectIsOwner);
  const accessibleItems = accessibleNavItems(adminSlugs, isOwner);
  const canAccess = (key: string) => accessibleItems.some((item) => item.key === key);
  const defaultPath = firstAccessiblePath(adminSlugs, isOwner);
  const [handShakeRetryCount, setHandShakeRetryCount] = useState(0);
  const dispatch = useAppDispatch();
  const [handshake] = useHandshakeMutation();
  if (!security.serverPublicKey && !SKIP_ENCRYPT && handShakeRetryCount < HAND_SHAKE_MAX_RETRY) {
    setHandShakeRetryCount((prevCount) => prevCount + 1);
    handShakeFunc(handshake).then((handshakeResult) => {
      dispatch(
        setSecurity({
          serverPublicKey: handshakeResult.serverPublicKey,
          localPublicKey: handshakeResult.localPublicKey,
          localPrivateKey: handshakeResult.localPrivateKey,
        }),
      );
    });
  }
  // Theme + document direction apply at the root so every route —
  // including the standalone auth pages — is themed and direction-aware.
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  // Re-runs on language change (i18next fires 'languageChanged') so direction
  // flips live when the user switches language from the header menu.
  useEffect(() => {
    const apply = () => {
      document.documentElement.dir = i18n.dir();
    };
    apply();
    i18n.on('languageChanged', apply);
    return () => i18n.off('languageChanged', apply);
  }, []);

  return (
    <>
      <Routes>
        {/* Standalone auth pages (outside the app shell). */}
        <Route path='/login' element={<Login />} />
        <Route path='/signup' element={<Signup />} />
        <Route path='/my-invites' element={<MyInvites />} />
        <Route path='/select-company' element={<SelectCompany />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/request-otp' element={<RequestOtp />} />
        <Route path='/verify-otp' element={<VerifyOtp />} />
        <Route path='/reset-password' element={<ResetPassword />} />
        <Route path='/set-password' element={<SetPassword />} />
        <Route path='/create-company' element={<CompanyCreation />} />
        {/* Shared-link resolver — loginToken-guarded inside the page itself
            (pre-company session, so it can't sit behind JWTProtectedRoute). */}
        <Route path='/bug-report/:id' element={<BugReportPage />} />
        {/* Emailed verification link. staticToken-authorized, so it must stay
            outside the session guard — it is normally opened in a fresh tab.
            Distinct from /verify-otp, which is the phone-keyed auth flow. */}
        <Route path='/verify-email' element={<VerifyEmailPage />} />

        {/* App shell — gated by JWTProtectedRoute (requires a company-scoped
            authToken; redirects to /login otherwise). */}
        <Route element={<JWTProtectedRoute />}>
          <Route element={<AuthenticatedLayout />}>
            <Route index element={<Navigate to={defaultPath} replace />} />
            {DEV_ONLY && <Route path='/theme' element={<ThemePage />} />}
            {/* Own profile — every signed-in user has one, so it is not
                module-gated and stays out of the nav-driven route list. */}
            <Route path='/my-profile' element={<MyProfilePage />} />
            {/* Sub-routes ride on their parent module's access. */}
            {canAccess('roles') && (
              <>
                <Route path='/roles/new' element={<RoleEditorPage />} />
                <Route path='/roles/:roleId' element={<RoleEditorPage />} />
              </>
            )}
            {canAccess('projects') && (
              <Route path='/projects/:projectId' element={<ProjectDetailPage />} />
            )}
            {canAccess('datasource') && (
              <Route path='/datasource/entries' element={<DataSourceEntriesPage />} />
            )}
            {canAccess('form-templates') && (
              <Route path='/form-templates/builder' element={<FormBuilderPage />} />
            )}
            {canAccess('reports') && <Route path='/submissions' element={<SubmissionsPage />} />}
            {canAccess('reports') && (
              <Route path='/submissions/view-report' element={<ReportViewPage />} />
            )}
            {/* One route per accessible nav item; the rest fall through to *. */}
            {accessibleItems.map((item) => {
              let element = <PlaceholderPage titleKey={item.labelKey} />;
              if (item.key === 'structure') element = <StructurePage />;
              else if (item.key === 'roles') element = <RolesPage />;
              else if (item.key === 'projects') element = <ProjectsPage />;
              else if (item.key === 'people') element = <PeoplePage />;
              else if (item.key === 'datasource') element = <DataSourcePage />;
              else if (item.key === 'data-structure') element = <DataStructurePage />;
              else if (item.key === 'data-setup') element = <DataSetupPage />;
              else if (item.key === 'inspections') element = <InspectionPage />;
              else if (item.key === 'form-templates') element = <FormTemplatesPage />;
              else if (item.key === 'reports') element = <ReportsPage />;
              else if (item.key === 'activity-log') element = <ActivityLogPage />;
              else if (item.key === 'trash') element = <TrashPage />;
              else if (item.key === 'attendance') element = <AttendancePage />;
              else if (item.key === 'settings') element = <CompanySettingsPage />;
              return <Route key={item.key} path={item.path} element={element} />;
            })}
            <Route path='*' element={<Navigate to={defaultPath} replace />} />
          </Route>
        </Route>
      </Routes>
      <Toaster position='bottom-center' toastOptions={{ duration: 2500 }} />
    </>
  );
}

export default App;
